import { VirtualElement } from '@popperjs/core';
import classNames from 'classnames';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';

export const CONTEXT_MENU_TRANSITION_LEAVE_TIME = 150;

const ContextMenuCloseContext = createContext<() => void>(() => undefined);
/** Closes the enclosing context menu; for items that shouldn't keep it open while they work. */
export const useCloseContextMenu = () => useContext(ContextMenuCloseContext);
interface ContextMenuProps {
  buttonChild: React.ReactNode;
  buttonClassName?: string;
  panelClassName?: string;
  popoverMenuDisabled?: boolean;
  anchorToPointer?: boolean;
}

const ContextMenu_: React.FC2<ContextMenuProps> = ({ buttonChild, buttonClassName, panelClassName, children, className, popoverMenuDisabled, anchorToPointer = true, ...props }) => {
  const [anchorPoint, setAnchorPoint] = useState<{ x: number; y: number } | null>(null);
  const onClose = useCallback(() => setAnchorPoint(null), []);

  const onContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (popoverMenuDisabled) return;
      setAnchorPoint({ x: event.clientX, y: event.clientY });
    },
    [popoverMenuDisabled],
  );

  return (
    <div onContextMenu={onContextMenu} className={classNames(className, buttonClassName)}>
      {buttonChild}
      {anchorPoint && (
        <ContextMenuOpen panelClassName={panelClassName} anchorToPointer={anchorToPointer} anchorPoint={anchorPoint} onClose={onClose} {...props}>
          {children}
        </ContextMenuOpen>
      )}
    </div>
  );
};

/** Only mounted when the context menu is open — avoids HSPopover global listeners on idle items */
const ContextMenuOpen: React.FC2<{
  panelClassName?: string;
  anchorToPointer?: boolean;
  anchorPoint: { x: number; y: number };
  onClose: () => void;
}> = ({ panelClassName, children, anchorToPointer = true, anchorPoint, onClose }) => {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const virtualReference = useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: anchorPoint.y,
        right: anchorPoint.x,
        bottom: anchorPoint.y,
        left: anchorPoint.x,
        x: anchorPoint.x,
        y: anchorPoint.y,
        toJSON: () => ({}),
      }),
    }),
    [anchorPoint],
  );
  const { styles, attributes } = usePopper(anchorToPointer ? virtualReference : undefined, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [
      { name: 'computeStyles', options: { gpuAcceleration: false } },
      { name: 'flip', options: { boundary: document.documentElement } },
      { name: 'preventOverflow', options: { boundary: document.documentElement } },
    ],
  });

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (popperElement && popperElement.contains(e.target as Node)) return;
      onClose();
    };
    // Capture phase: a press on something that stops its own propagation (a canvas starting a drag) still closes the menu.
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [onClose, popperElement]);

  return (
    <div
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
      className={classNames(panelClassName, 'absolute z-40 bg-white dark:bg-[#242424] p-2 rounded-md flex flex-col w-48 border border-gray-300 shadow-sm')}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ContextMenuCloseContext.Provider value={onClose}>{children}</ContextMenuCloseContext.Provider>
    </div>
  );
};

export const ContextMenu = memo(ContextMenu_);
